import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Input,
  Modal,
  Progress,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import OpsTeamHuddleContext from '@totem/components/ticketing/opsTeamHuddle/OpsTeamHuddleContext';
import { OpsHuddleProject } from '@totem/components/ticketing/opsTeamHuddle/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';

const { Text } = Typography;

const HtmlContent = ({ html }: { html: string }) => (
  <div dangerouslySetInnerHTML={{ __html: html }} />
);

const MIN_VALID_YEAR = 2000;

const isValidDate = (iso: string | null): boolean => {
  if (!iso) return false;
  return new Date(iso).getFullYear() >= MIN_VALID_YEAR;
};

const dateDiff = (iso: string | null): number | null => {
  if (!isValidDate(iso)) return null;
  return Math.floor((Date.now() - new Date(iso!).getTime()) / 86400000);
};

const formatDate = (iso: string | null): string => {
  if (!isValidDate(iso)) return '—';
  return new Date(iso!).toLocaleDateString();
};

type HealthColor = 'green' | 'amber' | 'red';

const computeHealth = (record: OpsHuddleProject): HealthColor => {
  const endDays = dateDiff(record.projectEnd);
  const isVeryOverdue = endDays !== null && endDays > 30;
  const isOverBudget =
    record.budgetHours > 0 && record.actualHours > record.budgetHours;
  if (
    isOverBudget ||
    isVeryOverdue ||
    (record.isStale && record.taskCount > 0)
  ) {
    return 'red';
  }
  const isOverdue = endDays !== null && endDays > 0;
  if (record.isStale || isOverdue || record.taskCount === 0) {
    return 'amber';
  }
  return 'green';
};

const healthLabel: Record<HealthColor, string> = {
  green: 'On Track',
  amber: 'Watch',
  red: 'At Risk',
};

const healthTagColor: Record<HealthColor, string> = {
  green: 'success',
  amber: 'warning',
  red: 'error',
};

interface KpiCardProps {
  label: string;
  value: string | number;
  sub: string;
  valueColor?: string;
}

const KpiCard = ({ label, value, sub, valueColor }: KpiCardProps) => (
  <div
    style={{
      background: 'var(--ant-color-bg-container)',
      border: '1px solid var(--ant-color-border)',
      borderRadius: 10,
      padding: '12px 14px',
      boxShadow: '0 1px 2px rgba(0,0,0,.05), 0 4px 12px rgba(0,0,0,.04)',
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--ant-color-text-secondary)',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: valueColor ?? 'var(--ant-color-text)',
        marginTop: 4,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 11,
        color: 'var(--ant-color-text-secondary)',
        marginTop: 6,
      }}
    >
      {sub}
    </div>
  </div>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 10.5,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: 'var(--ant-color-text-secondary)',
      fontWeight: 600,
    }}
  >
    {children}
  </div>
);

interface FieldBoxProps {
  label: string;
  value: React.ReactNode;
  valueColor?: string;
  extra?: React.ReactNode;
}

const FieldBox = ({ label, value, valueColor, extra }: FieldBoxProps) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div
      style={{
        fontSize: 13,
        marginTop: 2,
        color: valueColor ?? 'var(--ant-color-text)',
        fontWeight: valueColor ? 600 : undefined,
      }}
    >
      {value}
    </div>
    {extra && <div style={{ marginTop: 4 }}>{extra}</div>}
  </div>
);

const buildColumns = () => [
  {
    title: 'Health',
    key: 'health',
    width: 90,
    render: (_: unknown, record: OpsHuddleProject) => {
      const health = computeHealth(record);
      return <Tag color={healthTagColor[health]}>{healthLabel[health]}</Tag>;
    },
  },
  {
    title: 'Org',
    dataIndex: 'orgName',
    key: 'orgName',
    sorter: (aRow: OpsHuddleProject, bRow: OpsHuddleProject) =>
      aRow.orgName.localeCompare(bRow.orgName),
    width: 160,
    ellipsis: true,
  },
  {
    title: 'Building',
    dataIndex: 'buildingName',
    key: 'buildingName',
    width: 130,
    ellipsis: true,
    render: (val: string) => val || <Text type="secondary">—</Text>,
  },
  {
    title: 'Summary',
    dataIndex: 'summary',
    key: 'summary',
    ellipsis: true,
    render: (val: string) => (
      <Tooltip title={val}>
        <span>{val}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Service Category',
    key: 'serviceCategory',
    width: 150,
    ellipsis: true,
    render: (_: unknown, record: OpsHuddleProject) => (
      <span>
        {record.category || <Text type="secondary">—</Text>}
        {record.subCategory && (
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.subCategory}
            </Text>
          </div>
        )}
      </span>
    ),
  },
  {
    title: 'PM',
    dataIndex: 'pmName',
    key: 'pmName',
    width: 130,
    ellipsis: true,
    render: (val: string) => val || <Text type="secondary">—</Text>,
  },
  {
    title: 'Status',
    key: 'status',
    width: 100,
    render: (_: unknown, record: OpsHuddleProject) => {
      let label: string;
      let tagColor: string;
      const isDeferred =
        record.isOptimizeDeferred ||
        (record.followUpAt !== null &&
          new Date(record.followUpAt) > new Date());
      if (record.status === 'CLOSED') {
        label = 'Complete';

        tagColor = 'success';
      } else if (isDeferred) {
        label = 'Deferred';

        tagColor = 'purple';
      } else if (record.projectStatus?.toUpperCase() === 'PENDING') {
        label = 'Pending';

        tagColor = 'warning';
      } else {
        label = 'WIP';

        tagColor = 'blue';
      }
      return <Tag color={tagColor}>{label}</Tag>;
    },
  },
  {
    title: 'End Date',
    dataIndex: 'projectEnd',
    key: 'projectEnd',
    width: 100,
    sorter: (aRow: OpsHuddleProject, bRow: OpsHuddleProject) => {
      const aVal = aRow.projectEnd ?? '';
      const bVal = bRow.projectEnd ?? '';
      return aVal.localeCompare(bVal);
    },
    render: (val: string | null) => {
      if (!val) return <Text type="secondary">—</Text>;
      const days = dateDiff(val);
      const isOverdue = days !== null && days > 0;
      return (
        <Text type={isOverdue ? 'danger' : undefined}>{formatDate(val)}</Text>
      );
    },
  },
  {
    title: '#',
    dataIndex: 'number',
    key: 'number',
    width: 110,
    sorter: (aRow: OpsHuddleProject, bRow: OpsHuddleProject) =>
      aRow.number.localeCompare(bRow.number),
    render: (val: string, record: OpsHuddleProject) => (
      <Link to={`/dashboard/ticket/${record.id}`}>{val}</Link>
    ),
  },
  {
    title: 'Internal Note',
    key: 'internalNote',
    width: 260,
    render: (_: unknown, record: OpsHuddleProject) => {
      if (!record.latestInternalNote) return <Text type="secondary">—</Text>;
      const days = dateDiff(record.latestInternalNoteAt);
      const ago = days !== null ? ` (${days}d ago)` : '';
      const tooltipTitle = (
        <>
          <strong>
            {record.latestInternalNoteAuthor}
            {ago}:
          </strong>
          <HtmlContent html={record.latestInternalNote} />
        </>
      );
      return (
        <Tooltip title={tooltipTitle}>
          <div
            style={{
              maxHeight: 60,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            <HtmlContent html={record.latestInternalNote} />
          </div>
        </Tooltip>
      );
    },
  },
];

const OpsTeamHuddleLayout = () => {
  const { loading, data, onRefresh } = useContext(OpsTeamHuddleContext);
  const { getApiToken } = useContext(UserProfileContext);
  const [searchText, setSearchText] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('customer');
  const [activeTab, setActiveTab] = useState('ms');
  const [noteTargetId, setNoteTargetId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteSending, setNoteSending] = useState(false);

  const handleNoteSubmit = useCallback(() => {
    if (!noteTargetId || !noteText.trim()) return;

    setNoteSending(true);

    const body = {
      id: '',
      type: {
        id: '',
        code: 'INTERNAL_PROJECT_STATUS_NOTE',
        display: 'Internal Project Status Note',
      },
      notes: {
        isRichText: false,
        plainText: noteText.trim(),
        htmlText: noteText.trim(),
      },
      attributes: [],
      createdByType: 'User',
      createdByName: '',
      createdById: '',
      createdAt: new Date().toISOString(),
      isPrivate: false,
      keys: [],
    };

    fetch(`${TICKET_ENDPOINT}/${noteTargetId}/activity`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(body),
    }).then(() => {
      setNoteSending(false);

      setNoteTargetId(null);

      setNoteText('');

      onRefresh();
    });
  }, [getApiToken, noteTargetId, noteText, onRefresh]);

  const actionColumn = {
    title: '',
    key: 'actions',
    width: 60,
    fixed: 'right' as const,
    render: (_: unknown, record: OpsHuddleProject) => (
      <Button
        size="small"
        onClick={() => setNoteTargetId(record.id)}
        icon={<PlusCircleOutlined />}
      >
        Note
      </Button>
    ),
  };

  const projects = data?.projects ?? [];

  const orgOptions = useMemo(() => {
    const names = Array.from(
      new Set(projects.map((pro) => pro.orgName).filter(Boolean)),
    ).sort();
    return [
      { value: 'all', label: 'All customers' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [projects]);

  const categoryOptions = useMemo(() => {
    const cats = Array.from(
      new Set(projects.map((pro) => pro.category).filter(Boolean)),
    ).sort();
    return [
      { value: 'all', label: 'All service categories' },
      ...cats.map((cat) => ({ value: cat, label: cat })),
    ];
  }, [projects]);

  const pmOptions = useMemo(() => {
    const names = Array.from(
      new Set(projects.map((pro) => pro.pmName).filter(Boolean)),
    ).sort();
    return [
      { value: 'all', label: 'All leads' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [projects]);

  const healthOptions = [
    { value: 'all', label: 'All health' },
    { value: 'red', label: 'At Risk' },
    { value: 'amber', label: 'Watch' },
    { value: 'green', label: 'On Track' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All statuses' },
    { value: 'WIP', label: 'WIP' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Deferred', label: 'Deferred' },
    { value: 'Complete', label: 'Complete' },
  ];

  const sortOptions = [
    { value: 'customer', label: 'Sort: customer A→Z' },
    { value: 'health', label: 'Sort: worst health first' },
    { value: 'stale', label: 'Sort: stale first' },
    { value: 'recent', label: 'Sort: most recently updated' },
    { value: 'endDate', label: 'Sort: end date soonest' },
  ];

  const deriveStatusLabel = (pro: OpsHuddleProject): string => {
    if (pro.status === 'CLOSED') return 'Complete';
    if (
      pro.isOptimizeDeferred ||
      (pro.followUpAt !== null && new Date(pro.followUpAt) > new Date())
    )
      return 'Deferred';
    if (pro.projectStatus?.toUpperCase() === 'PENDING') return 'Pending';
    return 'WIP';
  };

  const filtered = useMemo(() => {
    const query = searchText.toLowerCase();
    let result = projects.filter((pro) => {
      if (healthFilter !== 'all' && computeHealth(pro) !== healthFilter)
        return false;
      if (orgFilter !== 'all' && pro.orgName !== orgFilter) return false;
      if (categoryFilter !== 'all' && pro.category !== categoryFilter)
        return false;
      if (pmFilter !== 'all' && pro.pmName !== pmFilter) return false;
      if (statusFilter !== 'all' && deriveStatusLabel(pro) !== statusFilter)
        return false;
      if (query) {
        const haystack = [
          pro.orgName,
          pro.summary,
          pro.category,
          pro.subCategory,
          pro.latestInternalNote,
          pro.number,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });

    const healthRank: Record<HealthColor, number> = {
      red: 0,
      amber: 1,
      green: 2,
    };
    result = [...result].sort((aRow, bRow) => {
      if (sortBy === 'health') {
        const diff =
          healthRank[computeHealth(aRow)] - healthRank[computeHealth(bRow)];
        if (diff !== 0) return diff;
      } else if (sortBy === 'stale') {
        const aStale = aRow.isStale ? 0 : 1;
        const bStale = bRow.isStale ? 0 : 1;
        if (aStale !== bStale) return aStale - bStale;
      } else if (sortBy === 'recent') {
        const aTime = aRow.latestActivityAt ?? '';
        const bTime = bRow.latestActivityAt ?? '';
        if (aTime !== bTime) return aTime > bTime ? -1 : 1;
      } else if (sortBy === 'endDate') {
        const aEnd = isValidDate(aRow.projectEnd)
          ? (aRow.projectEnd ?? '')
          : '';
        const bEnd = isValidDate(bRow.projectEnd)
          ? (bRow.projectEnd ?? '')
          : '';
        if (!aEnd && !bEnd) return 0;
        if (!aEnd) return 1;
        if (!bEnd) return -1;
        if (aEnd !== bEnd) return aEnd < bEnd ? -1 : 1;
      }
      return (
        aRow.orgName.localeCompare(bRow.orgName) ||
        aRow.summary.localeCompare(bRow.summary)
      );
    });

    return result;
  }, [
    projects,
    searchText,
    healthFilter,
    orgFilter,
    categoryFilter,
    pmFilter,
    statusFilter,
    sortBy,
  ]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Alert type="error" message="Failed to load Ops Huddle data." />;
  }

  const msCategories = new Set([
    'Managed Services',
    'Remote Access',
    'Backup / Threat Management',
    'FTE Consultant',
  ]);
  const msCount = filtered.filter((pro) =>
    msCategories.has(pro.category),
  ).length;
  const advCount = filtered.filter((pro) => pro.category === 'Advisory').length;
  const assessCount = filtered.filter(
    (pro) => pro.category === 'Assessment',
  ).length;

  const atRiskCount = filtered.filter(
    (pro) => computeHealth(pro) === 'red',
  ).length;
  const watchCount = filtered.filter(
    (pro) => computeHealth(pro) === 'amber',
  ).length;
  const onTrackCount = filtered.filter(
    (pro) => computeHealth(pro) === 'green',
  ).length;

  const readyToCloseCount = filtered.filter((pro) => {
    const allDone =
      pro.taskCount > 0 && pro.completedTaskCount >= pro.taskCount;
    if (allDone) return true;
    const endDays = dateDiff(pro.projectEnd);
    const overdueBy30 = endDays !== null && endDays >= 30;
    const hasProgress =
      pro.taskCount > 0 &&
      (pro.completedTaskCount > 0 || pro.activityCount > 0);
    return overdueBy30 && hasProgress;
  }).length;

  const noPmCount = filtered.filter((pro) => !pro.pmName).length;

  const feeInFlight = filtered.reduce((sum, pro) => sum + (pro.fee ?? 0), 0);

  const expandedRowRender = (record: OpsHuddleProject) => {
    const health = computeHealth(record);
    const burnPct =
      record.budgetHours > 0
        ? (record.actualHours / record.budgetHours) * 100
        : null;
    const progressPct =
      record.taskCount > 0
        ? (record.completedTaskCount / record.taskCount) * 100
        : null;

    const moveDates = [
      record.lastTimeEntry,
      record.lastTaskEnd,
      record.latestActivityAt,
    ].filter((dtm): dtm is string => Boolean(dtm));
    const lastMove =
      moveDates.length > 0
        ? moveDates.reduce((acc, dtm) => (acc > dtm ? acc : dtm))
        : null;
    const moveDays = lastMove ? dateDiff(lastMove) : null;

    const endDays = dateDiff(record.projectEnd);
    const endText =
      endDays === null
        ? '—'
        : endDays > 0
          ? `overdue by ${endDays}d`
          : `due in ${-endDays}d`;

    const reasons: { tier: 'red' | 'amber'; msg: string }[] = [];
    if (!record.pmName)
      reasons.push({ tier: 'amber', msg: 'No Project Manager assigned' });
    if (record.taskCount === 0)
      reasons.push({ tier: 'amber', msg: 'Zero tasks defined' });
    if (record.anyTaskOverBudget)
      reasons.push({ tier: 'amber', msg: 'A task is over budget' });
    if (record.isStale && record.taskCount > 0)
      reasons.push({ tier: 'red', msg: 'No movement in 30+ days' });
    if (endDays !== null && endDays > 30)
      reasons.push({ tier: 'red', msg: `End date overdue by ${endDays}d` });
    else if (endDays !== null && endDays > 0)
      reasons.push({ tier: 'amber', msg: `End date overdue by ${endDays}d` });

    const internalNoteDays = dateDiff(record.latestInternalNoteAt);
    const activityNoteDays = dateDiff(record.latestActivityAt);

    return (
      <div
        style={{
          borderLeft: '3px solid var(--ant-color-warning)',
          background: 'var(--ant-color-warning-bg)',
          padding: '12px 16px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '8px 22px',
          }}
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Health — {healthLabel[health]}</FieldLabel>
            {reasons.length === 0 ? (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--ant-color-text-secondary)',
                  marginTop: 3,
                }}
              >
                No risk signals — all checks clear.
              </div>
            ) : (
              <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                {reasons.map((rsn, idx) => (
                  <li
                    key={idx}
                    style={{
                      fontSize: 12,
                      color: 'var(--ant-color-text-secondary)',
                      lineHeight: 1.35,
                    }}
                  >
                    <Text
                      type={rsn.tier === 'red' ? 'danger' : 'warning'}
                      style={{ fontWeight: 600, fontSize: 11 }}
                    >
                      {rsn.tier.toUpperCase()}
                    </Text>
                    {' · '}
                    {rsn.msg}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <FieldBox
            label="Hours — budgeted"
            value={`${record.budgetHours} h`}
          />
          <FieldBox label="Hours — actual" value={`${record.actualHours} h`} />
          <FieldBox
            label="Burn"
            value={
              burnPct !== null ? `${burnPct.toFixed(0)}%` : 'no budget set'
            }
            extra={
              burnPct !== null ? (
                <Progress
                  percent={Math.min(burnPct, 100)}
                  size="small"
                  status={burnPct > 110 ? 'exception' : undefined}
                  showInfo={false}
                />
              ) : null
            }
          />

          <FieldBox
            label="Tasks"
            value={`${record.completedTaskCount} of ${record.taskCount} completed`}
          />
          <FieldBox
            label="Progress"
            value={
              progressPct !== null ? `${progressPct.toFixed(0)}%` : 'no tasks'
            }
            extra={
              progressPct !== null ? (
                <Progress
                  percent={Math.min(progressPct, 100)}
                  size="small"
                  showInfo={false}
                />
              ) : null
            }
          />
          <FieldBox
            label="Task over budget?"
            value={record.anyTaskOverBudget ? 'Yes' : 'No'}
            valueColor={record.anyTaskOverBudget ? '#faad14' : undefined}
          />

          <FieldBox
            label="Last movement"
            value={
              lastMove
                ? `${formatDate(lastMove)} (${moveDays}d ago)`
                : 'no activity logged'
            }
          />
          <FieldBox
            label="End date"
            value={
              record.projectEnd
                ? `${formatDate(record.projectEnd)} (${endText})`
                : '—'
            }
            valueColor={
              endDays !== null && endDays > 30
                ? '#ff4d4f'
                : endDays !== null && endDays > 0
                  ? '#faad14'
                  : undefined
            }
          />
          <FieldBox
            label="Fee"
            value={record.fee ? `$${record.fee.toLocaleString()}` : '—'}
          />
          <FieldBox label="Activity entries" value={record.activityCount} />
        </div>

        <div
          style={{
            borderTop: '1px dashed var(--ant-color-border)',
            marginTop: 10,
            paddingTop: 8,
          }}
        >
          <FieldLabel>Latest internal project status note (full)</FieldLabel>
          {record.latestInternalNote ? (
            <>
              <div style={{ marginTop: 6, fontSize: 13 }}>
                <HtmlContent html={record.latestInternalNote} />
              </div>
              {record.latestInternalNoteAt && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ant-color-text-secondary)',
                    marginTop: 4,
                  }}
                >
                  {`Posted ${formatDate(record.latestInternalNoteAt)} (${internalNoteDays}d ago)`}
                  {record.latestInternalNoteAuthor
                    ? ` by ${record.latestInternalNoteAuthor.split('@')[0]}`
                    : ''}
                </div>
              )}
            </>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No internal status note posted yet. Add one to this ticket in IB
              Portal.
            </Text>
          )}

          {record.latestActivityNote && (
            <>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--ant-color-text-secondary)',
                  marginTop: 10,
                }}
              >
                {`Most recent activity entry (any type)${record.latestActivityAt ? ` — ${formatDate(record.latestActivityAt)} · ${activityNoteDays}d ago` : ''}`}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--ant-color-text-secondary)',
                  marginTop: 4,
                }}
              >
                <HtmlContent html={record.latestActivityNote} />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const columns = [...buildColumns(), actionColumn];

  const projectTable = (rows: OpsHuddleProject[]) => (
    <Table<OpsHuddleProject>
      dataSource={rows}
      columns={columns}
      rowKey="id"
      size="small"
      pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      scroll={{ x: 'max-content' }}
      expandable={{ expandedRowRender }}
    />
  );

  const tabMs = filtered.filter((pro) => msCategories.has(pro.category));
  const tabAdv = filtered.filter((pro) => pro.category === 'Advisory');
  const tabAssess = filtered.filter((pro) => pro.category === 'Assessment');
  const tabQbr = filtered.filter(
    (pro) => pro.category === 'Quarterly Business Review',
  );
  const tabNeedsAttention = filtered.filter(
    (pro) => computeHealth(pro) === 'red' || computeHealth(pro) === 'amber',
  );

  const tabItems = [
    {
      key: 'ms',
      label: `Managed Services (${tabMs.length})`,
      children: projectTable(tabMs),
    },
    {
      key: 'adv',
      label: `Advisory (${tabAdv.length})`,
      children: projectTable(tabAdv),
    },
    {
      key: 'assess',
      label: `Assessment (${tabAssess.length})`,
      children: projectTable(tabAssess),
    },
    {
      key: 'qbr',
      label: `QBRs (${tabQbr.length})`,
      children: projectTable(tabQbr),
    },
    {
      key: 'hygiene',
      label: `Needs Attention (${tabNeedsAttention.length})`,
      children: projectTable(tabNeedsAttention),
    },
    {
      key: 'all',
      label: `All Open (${filtered.length})`,
      children: projectTable(filtered),
    },
  ];

  return (
    <ContentLayout>
      <IBCard isCovered title="Ops Team Huddle">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10,
            marginBottom: 16,
          }}
        >
          <KpiCard
            label="Open Projects"
            value={filtered.length}
            sub={`${msCount} MS · ${advCount} Advisory · ${assessCount} Assessment`}
          />
          <KpiCard
            label="At Risk"
            value={atRiskCount}
            sub="Burn >110%, no PM, no movement >28d, or zero tasks"
            valueColor={atRiskCount > 0 ? '#ff4d4f' : undefined}
          />
          <KpiCard
            label="Watch"
            value={watchCount}
            sub="Burn 80–110%, task over budget, or no movement >14d"
            valueColor={watchCount > 0 ? '#faad14' : undefined}
          />
          <KpiCard
            label="On Track"
            value={onTrackCount}
            sub="All health checks clear"
            valueColor="#52c41a"
          />
          <KpiCard
            label="Ready to Close"
            value={readyToCloseCount}
            sub="All tasks done, or 30d+ past end date"
            valueColor={readyToCloseCount > 0 ? '#13c2c2' : undefined}
          />
          <KpiCard
            label="No PM Assigned"
            value={noPmCount}
            sub="Active project, no PM"
            valueColor={noPmCount > 0 ? '#faad14' : undefined}
          />
          <KpiCard
            label="Fee in Flight"
            value={`$${(feeInFlight / 1000).toFixed(0)}k`}
            sub="Sum of project fee"
          />
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <Input.Search
            placeholder="Search customer, project, scope, note..."
            allowClear
            value={searchText}
            onChange={(evt) => setSearchText(evt.target.value)}
            style={{ minWidth: 260, flex: '1 1 260px' }}
          />
          <Select
            options={healthOptions}
            value={healthFilter}
            onChange={setHealthFilter}
            style={{ minWidth: 140 }}
          />
          <Select
            options={orgOptions}
            value={orgFilter}
            onChange={setOrgFilter}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ minWidth: 170 }}
          />
          <Select
            options={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            style={{ minWidth: 180 }}
          />
          <Select
            options={pmOptions}
            value={pmFilter}
            onChange={setPmFilter}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ minWidth: 150 }}
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ minWidth: 140 }}
          />
          <Select
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            style={{ minWidth: 200 }}
          />
        </div>
        <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
      </IBCard>
      <Modal
        title="Log Project Status Note"
        open={noteTargetId !== null}
        onCancel={() => {
          setNoteTargetId(null);

          setNoteText('');
        }}
        onOk={handleNoteSubmit}
        okText="Submit"
        okButtonProps={{ disabled: !noteText.trim(), loading: noteSending }}
        destroyOnClose
      >
        <Input.TextArea
          rows={5}
          placeholder="Enter status note..."
          value={noteText}
          onChange={(evt) => setNoteText(evt.target.value)}
        />
      </Modal>
    </ContentLayout>
  );
};

export default OpsTeamHuddleLayout;
